import React, { useEffect, useMemo, useRef } from 'react';
import { User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useOwnedSubmissions } from '../hooks/useOwnedSubmissions';
import { useTaskThumbnails } from '../hooks/useTaskThumbnails';
import {
  RawPhoto,
  useReleasedRawPhotos,
  useReleasedAnnotatedPhotos,
  useReleasedRawVideos,
} from './RawPhotoBrowse';
import './Dashboard.css';

type DashboardProps = {
  user: User;
};

type MarketplaceTab = 'photos' | 'datasets' | 'videos';

type InventoryTaskRow = {
  taskId: string;
  taskTitle: string;
  itemCount: number;
  labelCount: number;
};

type InventoryBox = {
  key: string;
  category: string;
  typeLabel: string;
  typeTab: MarketplaceTab;
  unit: string;
  showLabels: boolean;
  totalItems: number;
  totalLabels: number;
  topTasks: InventoryTaskRow[];
};

const FALLBACK_CATEGORY = 'Uncategorized';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Count items released to the marketplace in the last 7 days (ownership ignored). */
function countReleasedLast7Days(items: RawPhoto[]): number {
  const cutoff = Date.now() - WEEK_MS;
  return items.reduce((n, p) => {
    if (!p.releasedAt) return n;
    if (p.releasedAt.getTime() >= cutoff) return n + 1;
    return n;
  }, 0);
}

function buildBoxesForType(
  items: RawPhoto[],
  typeLabel: string,
  typeTab: MarketplaceTab,
  unit: string,
  showLabels: boolean
): InventoryBox[] {
  const byCategory = new Map<string, RawPhoto[]>();
  for (const item of items) {
    const cat = (item.category && String(item.category).trim()) || FALLBACK_CATEGORY;
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(item);
  }

  const boxes: InventoryBox[] = [];
  const sortedCats = Array.from(byCategory.keys()).sort((a, b) => a.localeCompare(b));

  for (const category of sortedCats) {
    const catItems = byCategory.get(category)!;
    // Group by taskId when present; fall back to title-only key
    const byTask = new Map<string, { taskId: string; taskTitle: string; items: RawPhoto[] }>();
    for (const item of catItems) {
      const taskId = (item.taskId && String(item.taskId).trim()) || '';
      const taskTitle = (item.taskTitle && String(item.taskTitle).trim()) || 'Untitled';
      const key = taskId || `title:${taskTitle}`;
      if (!byTask.has(key)) {
        byTask.set(key, { taskId, taskTitle, items: [] });
      }
      byTask.get(key)!.items.push(item);
    }

    const topTasks: InventoryTaskRow[] = Array.from(byTask.values())
      .map(({ taskId, taskTitle, items: taskItems }) => {
        const labelCount = showLabels
          ? taskItems.reduce((sum, p) => sum + (typeof p.labelCount === 'number' ? p.labelCount : 0), 0)
          : 0;
        return {
          taskId,
          taskTitle,
          itemCount: taskItems.length,
          labelCount,
        };
      })
      .sort((a, b) => b.itemCount - a.itemCount)
      .slice(0, 4);

    const totalLabels = showLabels
      ? catItems.reduce((sum, p) => sum + (typeof p.labelCount === 'number' ? p.labelCount : 0), 0)
      : 0;

    boxes.push({
      key: `${typeTab}::${category}`,
      category,
      typeLabel,
      typeTab,
      unit,
      showLabels,
      totalItems: catItems.length,
      totalLabels,
      topTasks,
    });
  }

  boxes.sort((a, b) => b.totalItems - a.totalItems || a.category.localeCompare(b.category));
  return boxes;
}

export default function Dashboard({ user }: DashboardProps) {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const {
    owned,
    rawOwnedIds,
    annotatedOwnedIds,
    rawVideoOwnedIds,
    loading: ownedLoading,
  } = useOwnedSubmissions(user.uid);

  const { photos: releasedRawPhotos, loading: rawAvailLoading } = useReleasedRawPhotos();
  const { photos: releasedAnnotated, loading: annotatedAvailLoading } = useReleasedAnnotatedPhotos();
  const { photos: releasedRawVideos, loading: videoAvailLoading } = useReleasedRawVideos();

  // One-shot verify that marketplace.*ReleasedAt is wired through as releasedAt
  const loggedReleasedAt = useRef(false);
  useEffect(() => {
    if (loggedReleasedAt.current) return;
    const sample =
      releasedRawPhotos[0] || releasedAnnotated[0] || releasedRawVideos[0] || null;
    if (!sample) return;
    loggedReleasedAt.current = true;
    console.log('[Dashboard] sample releasedAt check', {
      id: sample.id,
      releasedAt: sample.releasedAt,
      releasedAtISO: sample.releasedAt?.toISOString?.() ?? null,
      withReleasedAt: {
        raw: releasedRawPhotos.filter((p) => !!p.releasedAt).length,
        annotated: releasedAnnotated.filter((p) => !!p.releasedAt).length,
        video: releasedRawVideos.filter((p) => !!p.releasedAt).length,
      },
      totals: {
        raw: releasedRawPhotos.length,
        annotated: releasedAnnotated.length,
        video: releasedRawVideos.length,
      },
    });
  }, [releasedRawPhotos, releasedAnnotated, releasedRawVideos]);

  const ownedTotal = rawOwnedIds.size + annotatedOwnedIds.size + rawVideoOwnedIds.size;

  // Credits spent on purchases in the last 7 days (sum of creditsPaid)
  const creditsSpentThisWeek = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    return owned.reduce((sum, o) => {
      if (!o.purchasedAt || o.purchasedAt.getTime() < cutoff) return sum;
      return sum + (o.creditsPaid || 0);
    }, 0);
  }, [owned]);

  // Available to buy = marketplace-released minus already owned (matches marketplace tree)
  const availableByType = useMemo(() => {
    const raw = releasedRawPhotos.filter((p) => !rawOwnedIds.has(p.id)).length;
    const annotated = releasedAnnotated.filter((p) => !annotatedOwnedIds.has(p.id)).length;
    const video = releasedRawVideos.filter((p) => !rawVideoOwnedIds.has(p.id)).length;
    return { raw, annotated, video, total: raw + annotated + video };
  }, [
    releasedRawPhotos,
    releasedAnnotated,
    releasedRawVideos,
    rawOwnedIds,
    annotatedOwnedIds,
    rawVideoOwnedIds,
  ]);

  // New this week = released in last 7 days (ownership ignored — not purchase-affected)
  const newThisWeek = useMemo(() => {
    const raw = countReleasedLast7Days(releasedRawPhotos);
    const annotated = countReleasedLast7Days(releasedAnnotated);
    const video = countReleasedLast7Days(releasedRawVideos);
    return { raw, annotated, video, total: raw + annotated + video };
  }, [releasedRawPhotos, releasedAnnotated, releasedRawVideos]);

  // Marketplace-only inventory: one box per (type, category) with released items
  const inventoryBoxes = useMemo(() => {
    const rawBoxes = buildBoxesForType(
      releasedRawPhotos,
      'Raw Photos',
      'photos',
      'photos',
      false
    );
    const annotatedBoxes = buildBoxesForType(
      releasedAnnotated,
      'Annotated Photos',
      'datasets',
      'photos',
      true
    );
    const videoBoxes = buildBoxesForType(
      releasedRawVideos,
      'Raw Video',
      'videos',
      'videos',
      false
    );
    return [...rawBoxes, ...annotatedBoxes, ...videoBoxes];
  }, [releasedRawPhotos, releasedAnnotated, releasedRawVideos]);

  // Resolve tasks/{taskId}.sampleImageUrl || icon for visible inventory tasks (shared cache).
  const inventoryTaskIds = useMemo(
    () => inventoryBoxes.flatMap((b) => b.topTasks.map((t) => t.taskId).filter(Boolean)),
    [inventoryBoxes]
  );
  const taskThumbById = useTaskThumbnails(inventoryTaskIds);

  const inventoryLoading = rawAvailLoading || annotatedAvailLoading || videoAvailLoading;

  const loading =
    ownedLoading || rawAvailLoading || annotatedAvailLoading || videoAvailLoading;

  const typeRow = (
    label: string,
    value: string | number,
    loadingFlag: boolean,
    onClick: () => void
  ) => (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        margin: '2px 0',
        padding: '8px 4px',
        borderRadius: 6,
        cursor: 'pointer',
        minWidth: 0,
        transition: 'background 0.12s ease',
      }}
      onMouseEnter={(e) => {
        const row = e.currentTarget;
        row.style.background = `var(--bg-accent, ${colors.primary}18)`;
        row.querySelectorAll<HTMLElement>('[data-row-tint]').forEach((node) => {
          node.style.color = colors.primary;
        });
      }}
      onMouseLeave={(e) => {
        const row = e.currentTarget;
        row.style.background = 'transparent';
        row.querySelectorAll<HTMLElement>('[data-row-tint]').forEach((node) => {
          node.style.color = node.dataset.rowTint || colors.text;
        });
      }}
    >
      <span data-row-tint={colors.text} style={{ color: colors.text, fontSize: 14 }}>
        {label}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <strong data-row-tint={colors.text} style={{ color: colors.text, fontSize: 15 }}>
          {loadingFlag ? '…' : Number(value).toLocaleString()}
        </strong>
        <span
          data-row-tint={colors.secondary}
          style={{ color: colors.secondary, fontSize: 18, lineHeight: 1 }}
          aria-hidden
        >
          ›
        </span>
      </span>
    </div>
  );

  return (
    <div
      className="client-dashboard"
      style={{
        background: colors.background,
        color: colors.text,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <div className="dashboard-header" style={{ minWidth: 0, maxWidth: '100%' }}>
        <div style={{ minWidth: 0 }}>
          <h1>Data & Credits Overview</h1>
          <p>Monitor your library, marketplace inventory, and available data by category.</p>
        </div>
      </div>

      {loading && <div className="dashboard-loading">Loading your console data...</div>}

      {/* Top stat strip */}
      <section
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          marginBottom: 24,
        }}
      >
        {[
          {
            label: 'Credits Spent — This Week',
            value: ownedLoading ? '…' : creditsSpentThisWeek.toLocaleString(),
          },
          {
            label: 'Items You Own',
            value: ownedLoading ? '…' : ownedTotal.toLocaleString(),
          },
          {
            label: 'Available to Buy',
            value:
              inventoryLoading || ownedLoading ? '…' : availableByType.total.toLocaleString(),
          },
          {
            label: 'New This Week',
            value:
              inventoryLoading || ownedLoading ? '…' : newThisWeek.total.toLocaleString(),
          },
        ].map((tile) => (
          <article
            key={tile.label}
            className="dashboard-panel"
            style={{
              borderColor: colors.border,
              flex: '1 1 160px',
              minWidth: 0,
              padding: '16px 18px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                fontSize: 12,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: colors.secondary,
                marginBottom: 6,
                minHeight: 32,
                lineHeight: 1.25,
              }}
            >
              {tile.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
              {tile.value}
            </div>
          </article>
        ))}
      </section>

      {/* Three cards: Library / Available / New last 7 days */}
      <section
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          marginBottom: 24,
        }}
      >
        <article
          className="dashboard-panel"
          style={{
            borderColor: colors.border,
            flex: '1 1 220px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Your Library</h2>
          {typeRow('Raw Photos', rawOwnedIds.size, ownedLoading, () =>
            navigate('/library', { state: { libTab: 'rawPhoto' } })
          )}
          {typeRow('Annotated Photos', annotatedOwnedIds.size, ownedLoading, () =>
            navigate('/library', { state: { libTab: 'annotatedPhoto' } })
          )}
          {typeRow('Raw Video', rawVideoOwnedIds.size, ownedLoading, () =>
            navigate('/library', { state: { libTab: 'rawVideo' } })
          )}
        </article>

        <article
          className="dashboard-panel"
          style={{
            borderColor: colors.border,
            flex: '1 1 220px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Available in Marketplace</h2>
          {typeRow(
            'Raw Photos',
            availableByType.raw,
            rawAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'photos' } })
          )}
          {typeRow(
            'Annotated Photos',
            availableByType.annotated,
            annotatedAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'datasets' } })
          )}
          {typeRow(
            'Raw Video',
            availableByType.video,
            videoAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'videos' } })
          )}
        </article>

        <article
          className="dashboard-panel"
          style={{
            borderColor: colors.border,
            flex: '1 1 220px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>New — Last 7 Days</h2>
          {typeRow(
            'Raw Photos',
            newThisWeek.raw,
            rawAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'photos' } })
          )}
          {typeRow(
            'Annotated Photos',
            newThisWeek.annotated,
            annotatedAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'datasets' } })
          )}
          {typeRow(
            'Raw Video',
            newThisWeek.video,
            videoAvailLoading || ownedLoading,
            () => navigate('/marketplace', { state: { typeTab: 'videos' } })
          )}
        </article>
      </section>

      {/* Data Inventory — marketplace-released only, boxes by (type, category) */}
      <section
        id="data-inventory"
        className="dashboard-panel"
        style={{
          borderColor: colors.border,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <div className="panel-header" style={{ minWidth: 0, maxWidth: '100%' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2>Data Inventory by Category</h2>
            <p>Marketplace-released items grouped by type and category.</p>
          </div>
        </div>

        {inventoryLoading ? (
          <div className="empty-state">Loading marketplace inventory...</div>
        ) : inventoryBoxes.length === 0 ? (
          <div className="empty-state">
            No items are released to the marketplace yet. Check back soon.
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: 16,
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              marginTop: 16,
              paddingBottom: 8,
            }}
          >
            {inventoryBoxes.map((box) => (
              <InventoryBoxCard
                key={box.key}
                box={box}
                colors={colors}
                thumbByTaskId={taskThumbById}
                onBrowse={() =>
                  navigate('/marketplace', {
                    state: {
                      categoryFilter: box.category,
                      typeTab: box.typeTab,
                    },
                  })
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

type InventoryBoxCardProps = {
  box: InventoryBox;
  colors: any;
  thumbByTaskId: Record<string, string>;
  onBrowse: () => void;
};

function InventoryBoxCard({ box, colors, thumbByTaskId, onBrowse }: InventoryBoxCardProps) {
  const unitSingular = box.unit === 'videos' ? 'video' : 'photo';

  return (
    <article
      className="category-card"
      style={{
        borderColor: colors.border,
        flex: '0 0 300px',
        flexShrink: 0,
        width: 300,
        minWidth: 300,
        maxWidth: 300,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div className="category-card__header" style={{ minWidth: 0 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {box.category} · {box.typeLabel}
          </h3>
          <p className="category-stats">
            {box.totalItems.toLocaleString()} {box.unit}
            {box.showLabels
              ? ` · ${box.totalLabels.toLocaleString()} labels`
              : ''}
          </p>
        </div>
      </div>

      <div className="category-card__body">
        {box.topTasks.length === 0 ? (
          <div className="empty-subcategories">No tasks yet</div>
        ) : (
          <ul className="task-thumbnail-list">
            {box.topTasks.map((task) => {
              const thumbUrl = task.taskId ? thumbByTaskId[task.taskId] || '' : '';
              const resolved = task.taskId ? Object.prototype.hasOwnProperty.call(thumbByTaskId, task.taskId) : true;
              return (
                <li key={task.taskId || task.taskTitle} className="task-thumbnail-row">
                  <div className="task-thumb-ring" style={{ borderColor: colors.primary }}>
                    {thumbUrl ? (
                      <img src={thumbUrl} alt="" />
                    ) : (
                      <span
                        className="task-thumb-placeholder"
                        style={{ background: colors.border, color: colors.secondary }}
                      >
                        {resolved ? (task.taskTitle || '?').charAt(0).toUpperCase() : ''}
                      </span>
                    )}
                  </div>
                  <div className="task-thumbnail-meta">
                    <div className="task-thumbnail-name" style={{ color: colors.text }}>
                      {task.taskTitle}
                    </div>
                    <div className="task-thumbnail-stats" style={{ color: colors.secondary }}>
                      {task.itemCount.toLocaleString()}{' '}
                      {task.itemCount === 1 ? unitSingular : box.unit}
                      {box.showLabels && task.labelCount > 0
                        ? ` · ${task.labelCount.toLocaleString()} label${task.labelCount === 1 ? '' : 's'}`
                        : ''}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="category-card__footer">
        <button
          className="nav-topup-btn"
          style={{ background: colors.primary }}
          onClick={onBrowse}
        >
          Browse {box.category} →
        </button>
      </div>
    </article>
  );
}
